/**
 * Éditeur d'overlay + prévisualisation temps réel.
 *
 * La prévisualisation est rendue aux DIMENSIONS RÉELLES de la vidéo (1920×1080
 * paysage / 1080×1920 portrait) puis mise à l'échelle (transform scale) pour
 * tenir dans la colonne. Conséquence : les tailles de police sont EXACTEMENT
 * proportionnelles à la vidéo finale (mêmes px que le filtergraph FFmpeg) — donc
 * petites et fidèles, plus de texte géant.
 *
 * Layout (demandé) : niveau · matière en haut, titre juste en dessous ; le vrai
 * téléphone (iframe de capture) + le fond image ; logo « JeRaisonne » en bas à
 * gauche avec le QR Code juste à côté ; URL au-dessus.
 */

import { useEffect, useRef, useState } from 'react';
import { cn, CompassLogo } from '@jeraisonne/ui';
import { QrCode } from 'lucide-react';
import type { VideoOverlay, AspectRatio } from '@jeraisonne/db/schemas/youtube';
import { captureUrl } from './capture-preview';
import { inputCls } from './ui';

/** Fond signé pour l'aperçu : image ou vidéo. */
export type PreviewBackground = { url: string; isVideo: boolean } | null;

interface OverlayEditorProps {
  value: VideoOverlay;
  aspect: AspectRatio;
  onChange: (next: VideoOverlay) => void;
  /** Fond signé (image OU vidéo) pour la preview, si dispo. */
  background?: PreviewBackground;
  /** Attempt / exercice à rejouer dans le téléphone de l'aperçu. */
  attemptId?: string | null;
  exerciseId?: string | null;
}

export function OverlayEditor({
  value,
  aspect,
  onChange,
  background,
  attemptId,
  exerciseId,
}: OverlayEditorProps) {
  const set = <K extends keyof VideoOverlay>(key: K, v: VideoOverlay[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {/* Champs */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Niveau">
            <input
              className={inputCls}
              value={value.level}
              onChange={(e) => set('level', e.target.value)}
              placeholder="4e"
            />
          </Field>
          <Field label="Matière">
            <input
              className={inputCls}
              value={value.subject}
              onChange={(e) => set('subject', e.target.value)}
              placeholder="Maths"
            />
          </Field>
        </div>
        <Field label="Titre vidéo">
          <input
            className={inputCls}
            value={value.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Ex : Le théorème de Pythagore"
          />
        </Field>
        <Field label="Sous-titre">
          <input
            className={inputCls}
            value={value.subtitle}
            onChange={(e) => set('subtitle', e.target.value)}
            placeholder="Révise en t’amusant"
          />
        </Field>
        <Field label="URL affichée / QR">
          <input
            className={inputCls}
            value={value.qr_url}
            onChange={(e) => set('qr_url', e.target.value)}
            placeholder="https://jeraisonne.app"
          />
        </Field>
        <div className="flex flex-wrap gap-2 pt-1">
          <Toggle
            label="Logo"
            active={value.show_logo}
            onClick={() => set('show_logo', !value.show_logo)}
          />
          <Toggle
            label="QR Code"
            active={value.show_qr}
            onClick={() => set('show_qr', !value.show_qr)}
          />
          <Toggle
            label="URL"
            active={value.show_url}
            onClick={() => set('show_url', !value.show_url)}
          />
        </div>
      </div>

      {/* Prévisualisation temps réel */}
      <div>
        <p className="font-display text-ink-400 mb-2 text-[11px] font-bold uppercase tracking-widest">
          Aperçu (échelle réelle)
        </p>
        <OverlayPreview
          value={value}
          aspect={aspect}
          background={background}
          attemptId={attemptId}
          exerciseId={exerciseId}
        />
      </div>
    </div>
  );
}

export function OverlayPreview({
  value,
  aspect,
  background,
  attemptId,
  exerciseId,
}: {
  value: VideoOverlay;
  aspect: AspectRatio;
  background?: PreviewBackground;
  attemptId?: string | null;
  exerciseId?: string | null;
}) {
  const portrait = aspect === 'portrait';
  // Dimensions logiques = vidéo finale (mêmes que le worker / filtergraph).
  const BASE_W = portrait ? 1080 : 1920;
  const BASE_H = portrait ? 1920 : 1080;

  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.2);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => el.clientWidth > 0 && setScale(el.clientWidth / BASE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [BASE_W]);

  // Géométrie du téléphone — calquée sur buildCompositionPlan.
  const phoneH = portrait ? Math.round(BASE_H * 0.64) : Math.round(BASE_H * 0.89);
  const phoneW = Math.round((phoneH * 9) / 19.5);
  const phoneRight = portrait ? undefined : 120;
  const phoneLeft = portrait ? (BASE_W - phoneW) / 2 : undefined;
  const phoneTop = (BASE_H - phoneH) / 2;

  const hasPhone = !!(attemptId || exerciseId);

  return (
    <div ref={wrapRef} className="w-full" style={{ aspectRatio: `${BASE_W} / ${BASE_H}` }}>
      {/* Plan rendu à BASE_W×BASE_H puis scalé : tout est proportionnel à la vidéo. */}
      <div
        className="bg-ink-800 relative overflow-hidden rounded-xl text-white shadow-inner"
        style={{
          width: BASE_W,
          height: BASE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {/* Fond : image ou vidéo (bouclée, muette) */}
        {background &&
          (background.isVideo ? (
            <video
              src={background.url}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <img
              src={background.url}
              alt="Fond"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ))}
        {/* Voile lisibilité */}
        <div className="bg-linear-to-br absolute inset-0 from-black/30 via-transparent to-black/45" />

        {/* Téléphone réel (iframe de capture) */}
        {hasPhone && (
          <div
            className="absolute overflow-hidden rounded-[48px] border-8 border-black/30 bg-black/20 shadow-2xl"
            style={{
              width: phoneW,
              height: phoneH,
              top: phoneTop,
              left: phoneLeft,
              right: phoneRight,
            }}
          >
            <iframe
              title="Téléphone"
              src={captureUrl({ attemptId, exerciseId, embed: true })}
              className="h-full w-full border-0"
              sandbox="allow-scripts allow-same-origin"
              scrolling="no"
            />
          </div>
        )}

        {/* Empilement haut-gauche : niveau · matière, puis titre, puis sous-titre */}
        <div className="absolute left-16 top-14 flex max-w-[58%] flex-col gap-3">
          {(value.level || value.subject) && (
            <div className="flex items-center gap-3">
              {value.level && (
                <span className="rounded-xl bg-black/50 px-4 py-1.5 text-[30px] font-bold leading-none">
                  {value.level}
                </span>
              )}
              {value.subject && (
                <span className="text-[30px] font-bold leading-none text-white/90 drop-shadow">
                  {value.subject}
                </span>
              )}
            </div>
          )}
          {value.title && (
            <p className="font-display w-fit rounded-xl bg-black/50 px-5 py-3 text-[46px] font-extrabold leading-tight">
              {value.title}
            </p>
          )}
          {value.subtitle && (
            <p className="text-[26px] font-medium text-white/90 drop-shadow">{value.subtitle}</p>
          )}
        </div>

        {/* Bas-gauche : [logo] JeRaisonne [QR] — compact ; URL au-dessus */}
        <div className="absolute bottom-14 left-16 flex flex-col gap-3">
          {value.show_url && value.qr_url && (
            <span className="w-fit rounded-lg bg-black/50 px-4 py-1.5 text-[22px] font-medium">
              {value.qr_url}
            </span>
          )}
          <div className="flex items-center gap-4">
            {value.show_logo && (
              <div className="flex items-center gap-3 rounded-xl bg-black/55 px-4 py-2">
                <CompassLogo className="h-[44px] w-[44px]" />
                <span className="font-display text-[30px] font-extrabold">JeRaisonne</span>
              </div>
            )}
            {value.show_qr && (
              <div className="text-ink-900 flex h-[80px] w-[80px] items-center justify-center rounded-xl bg-white p-1.5">
                <QrCode className="h-full w-full" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="font-display text-ink-600 mb-1 block text-xs font-bold">{label}</span>
      {children}
    </label>
  );
}

function Toggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-9 items-center rounded-lg border px-3 text-xs font-bold transition-colors active:scale-[.98]',
        active
          ? 'border-primary-200 bg-primary-50 text-primary-700'
          : 'border-ink-200 text-ink-400 bg-white',
      )}
    >
      {label}
    </button>
  );
}
